import axios from "axios";
import Cookies from "universal-cookie";
import i18n from "../i18n";
import { appMessage as message } from "./appMessage";
import { markNotified } from "./notifiedError";

// Get base URL from environment or use relative URL (empty string)
// Using relative URLs means requests go to the same server as the frontend
const API_BASE_URL = process.env.REACT_APP_API_URL || "";
const cookies = new Cookies(null, { path: "/" });
const AUTH_SESSION_STORAGE_KEY = "authSessionActive";
// ponytail: public paths where stale cookies should not force redirect
const PUBLIC_PATHS = ["/login", "/forgot-password", "/reset-password", "/signup"];

// Shared in-flight refresh so concurrent callers in this tab reuse one request.
// Refresh tokens are single-use (rotated server-side), so a second request with
// the same token would fail and log the user out.
let refreshPromise = null;

export const hasPersistedAuthSession = () =>
  window.localStorage.getItem(AUTH_SESSION_STORAGE_KEY) === "true";

export const markPersistedAuthSession = () => {
  window.localStorage.setItem(AUTH_SESSION_STORAGE_KEY, "true");
};

export const clearPersistedAuthSession = () => {
  window.localStorage.removeItem(AUTH_SESSION_STORAGE_KEY);
};

const getLocalizedMessage = (
  payload,
  fallbackKey,
  fallbackText,
  fallbackParams,
) => {
  if (payload?.i18nKey) {
    return i18n.t(payload.i18nKey, payload.params || {});
  }

  if (payload?.error) {
    return payload.error;
  }

  if (payload?.message) {
    return payload.message;
  }

  return i18n.t(fallbackKey, fallbackParams || {}) || fallbackText;
};

// Create axios instance
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
  timeout: 600000,
});

const requestRefresh = async (refreshTokenAtCall) => {
  // Another tab may have rotated the tokens while we waited for the lock.
  const currentRefresh = cookies.get("refresh");
  const currentToken = cookies.get("token");
  if (currentRefresh !== refreshTokenAtCall && currentRefresh && currentToken) {
    return currentToken;
  }
  if (!currentRefresh) throw new Error("No refresh token");

  // Use raw axios (not apiClient) to bypass interceptors — a refresh failure
  // must not re-trigger another refresh attempt or show duplicate toasts.
  const { data } = await axios.post(`${API_BASE_URL}api/auth/refresh`, {
    refreshToken: currentRefresh,
  });

  if (data.ok && data.token) {
    cookies.set("token", data.token, { path: "/", maxAge: 7 * 24 * 60 * 60 });
    cookies.set("refresh", data.refreshToken, {
      path: "/",
      maxAge: 7 * 24 * 60 * 60,
    });
    markPersistedAuthSession();
    return data.token;
  }
  throw new Error("No token received");
};

// Function to refresh the access token. Single-flight within the tab, and
// serialized across tabs with the Web Locks API (cookies are shared by tabs).
export const refreshAccessToken = () => {
  if (!refreshPromise) {
    const refreshTokenAtCall = cookies.get("refresh");
    const run = () => requestRefresh(refreshTokenAtCall);
    const pending = navigator.locks
      ? navigator.locks.request("auth-refresh", run)
      : run();
    refreshPromise = pending.finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
};

// Refresh never reached the server (offline, server restarting) — the session
// may still be valid, so callers must not log the user out.
export const isNetworkFailure = (err) => !!err?.request && !err?.response;

// Idempotent: only the first failing caller still has the refresh cookie,
// so the toast and redirect happen once.
const endExpiredSession = () => {
  if (!cookies.get("refresh")) return;
  clearPersistedAuthSession();
  message.error(i18n.t("api.sessionExpired"));
  cookies.remove("token");
  cookies.remove("refresh");
  if (!PUBLIC_PATHS.includes(window.location.pathname)) {
    window.location.href = "/login";
  }
};

// Request interceptor - Add auth token to all requests
apiClient.interceptors.request.use(
  (config) => {
    // Get latest token from cookies (may have been refreshed)
    const token = cookies.get("token");

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Response interceptor - Handle errors globally
apiClient.interceptors.response.use(
  (response) => {
    // Return response data directly
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    if (error.response) {
      const { status, data } = error.response;

      // Handle specific error codes
      switch (status) {
        case 401:
          // Unauthorized - token expired or invalid
          if (data.code === "TOKEN_EXPIRED" && !originalRequest._retry) {
            originalRequest._retry = true;
            const currentToken = cookies.get("token");
            const sentAuth = originalRequest.headers?.Authorization;

            // Token was already refreshed (by another request or tab) after
            // this request was sent — retry with it instead of refreshing.
            if (currentToken && sentAuth !== `Bearer ${currentToken}`) {
              originalRequest.headers.Authorization = `Bearer ${currentToken}`;
              return apiClient(originalRequest);
            }

            if (!hasPersistedAuthSession() || !cookies.get("refresh")) {
              return Promise.reject(error);
            }

            try {
              const token = await refreshAccessToken();
              originalRequest.headers.Authorization = `Bearer ${token}`;
              return apiClient(originalRequest);
            } catch (err) {
              if (!isNetworkFailure(err)) endExpiredSession();
              return Promise.reject(err);
            }
          } else if (data.code === "NO_TOKEN") {
            message.error(i18n.t("api.loginRequired"));
            if (!PUBLIC_PATHS.includes(window.location.pathname)) {
              window.location.href = "/login";
            }
          } else {
            message.error(
              getLocalizedMessage(
                data,
                "api.authRequired",
                "Authentication required",
              ),
            );
          }
          break;

        case 403:
          // Forbidden - user doesn't have permission
          if (data.code === "ACCOUNT_NOT_ACTIVE") {
            message.error(
              i18n.t("api.accountStatus", {
                status: data.status || "inactive",
              }),
            );
            // localStorage.removeItem("authToken");
            cookies.remove("token");
            if (!PUBLIC_PATHS.includes(window.location.pathname)) {
              window.location.href = "/login";
            }
          } else if (
            data.code === "COOKIE_POLICY_BLOCKED" ||
            data.code === "COOKIE_CONSENT_REQUIRED" ||
            data.code === "COOKIE_CONSENT_VERSION_MISMATCH" ||
            data.code === "LOCAL_LOGIN_DISABLED_BY_COOKIE_POLICY" ||
            data.code === "OPENID_LOGIN_DISABLED_BY_COOKIE_POLICY"
          ) {
            message.warning(
              getLocalizedMessage(
                data,
                "api.cookieUpdateRequired",
                "Cookie consent update required",
              ),
            );
            window.location.href =
              data.cookieSettingsPath || "/cookie-settings";
          } else if (data.code === "NOT_ADMIN") {
            message.error(i18n.t("api.adminRequired"));
          } else {
            message.error(
              getLocalizedMessage(data, "api.accessDenied", "Access denied"),
            );
          }
          break;

        case 404:
          message.error(
            getLocalizedMessage(
              data,
              "api.resourceNotFound",
              "Resource not found",
            ),
          );
          break;

        case 409:
          // Conflict - usually duplicate data
          message.error(
            getLocalizedMessage(data, "api.conflict", "Conflict error"),
          );
          break;

        case 500:
          message.error(i18n.t("api.serverError"));
          break;

        default:
          message.error(
            getLocalizedMessage(data, "api.genericError", "An error occurred"),
          );
      }
    } else if (error.request) {
      // Request made but no response received
      message.error(i18n.t("api.noResponse"));
    } else {
      // Something else happened
      message.error(error.message || i18n.t("api.genericError"));
    }

    // Every path reaching here has shown a toast above; let callers skip theirs.
    return Promise.reject(markNotified(error));
  },
);

// Helper functions for common HTTP methods
export const api = {
  get: (url, config) => apiClient.get(url, config),
  post: (url, data, config) => apiClient.post(url, data, config),
  put: (url, data, config) => apiClient.put(url, data, config),
  delete: (url, config) => apiClient.delete(url, config),
  patch: (url, data, config) => apiClient.patch(url, data, config),
};

// Export both the axios instance and helper
export default apiClient;
